/**
 * Rental DTO + related lookups exposed by rental-service `/api/rentals`.
 * Mirrors `net.nawam.rental.model.entity.Rental` filtered by `RenRead` /
 * `RenReadDetail` JsonViews.
 */

import type { UserRef } from './user.types';
import type { DocusignStatus, Image, Vehicle } from './vehicle.types';

/** All possible rental workflow statuses (see `RentalStatusEnum`). */
export type RentalStatus =
  | 'PREPENDING'
  | 'PENDING'
  | 'CONFIRMED'
  | 'STARTED'
  | 'FINISHED'
  | 'CANCELLED';

/** Customer (renter) reference — minimal subset useful for admin display. */
export interface CustomerRef {
  id: number;
  userId?: number | null;
  /** Read-model du user lié (firstName, lastName, email…). */
  user?: UserRef | null;
  licenseNumber?: string | null;
  licenseExpiryDate?: string | null;
  licenseIssuingCountry?: string | null;
  licenseIssuingState?: string | null;
  dateOfBirth?: string | null;
  address?: string | null;
  city?: string | null;
  postalCode?: string | null;
  country?: string | null;
  emergencyContactName?: string | null;
  emergencyContactPhone?: string | null;
  emergencyContactRelation?: string | null;
  verificationStatus?: 'PENDING' | 'VERIFIED' | 'REJECTED' | null;
  verifiedAt?: string | null;
  verifiedBy?: number | null;
  rejectionReason?: string | null;
  totalRentals?: number;
  averageRating?: number | null;
}

export interface Rental {
  // ── BaseEntity ───────────────────────────────────────────────────────
  id: number;
  createdAt?: string | null;
  updatedAt?: string | null;

  // ── parties ───────────────────────────────────────────────────────────
  /** Deprecated. Use `customer.userId` instead. */
  userId?: number | null;
  customer?: CustomerRef | null;
  vehicle: Vehicle | null;

  // ── workflow ──────────────────────────────────────────────────────────
  rentalStatusEnum: RentalStatus;

  // ── dates & cost ──────────────────────────────────────────────────────
  startDate: string;
  endDate: string;
  nbHours: number;
  pickupLocation: string;
  returnLocation: string;
  totalCost: number;

  // ── contract / signatures ─────────────────────────────────────────────
  qrcode?: string | null;
  docusignSubmissionStatus?: DocusignStatus | null;
  docusignSubmissionId?: number | null;
  docusignSubmissionLink?: string | null;
  rentalContract?: string | null;

  // ── post-rental ───────────────────────────────────────────────────────
  averageRating?: number | null;
  lateFee?: number | null;
  actualReturnDate?: string | null;

  // ── photos (detail) ───────────────────────────────────────────────────
  photosBefore?: Image[];
  photosAfter?: Image[];
}

import { defensiveStatusMeta } from '../helpers/status-meta';

/** UI helper : maps a rental status to its label + Tailwind palette suffix.
 *  Defensive: unknown/null statuses fall back to `{label:'—', palette:'gray'}`. */
export const RENTAL_STATUS_META = defensiveStatusMeta<RentalStatus>({
  PREPENDING: { label: 'Pré-réservation', palette: 'gray' },
  PENDING: { label: 'En attente', palette: 'warning' },
  CONFIRMED: { label: 'Confirmée', palette: 'brand' },
  STARTED: { label: 'En cours', palette: 'blue-light' },
  FINISHED: { label: 'Terminée', palette: 'gray' },
  CANCELLED: { label: 'Annulée', palette: 'error' },
});
